/**
 * Station Entity
 *
 * A pure domain entity representing an ATC station.
 * Contains business rules and invariants - no framework or persistence dependencies.
 */

import type { AtcRadioEffectPresetInput } from '@/types/AtcRadioEffect';

import { Scenario } from './Scenario';

export interface Station {
  readonly id: string;
  readonly airportCode: string;
  readonly stationType: string;
  readonly displayName: string;
  readonly airportName?: string | null;
  readonly iataCode?: string | null;
  readonly icaoCode?: string | null;
  readonly localCode?: string | null;
  readonly city?: string | null;
  readonly country?: string | null;
  readonly timeZone?: string | null;
  readonly radioEffectPreset?: AtcRadioEffectPresetInput;
  readonly scenarioCount: number;
  readonly totalSizeBytes: number;
  readonly manifestUrl: string;
  readonly updatedAt: Date;
  readonly scenarios?: readonly Scenario[];
}

/**
 * Check if a station has loaded scenarios.
 */
export function hasScenarios(station: Station): boolean {
  return station.scenarios !== undefined && station.scenarios.length > 0;
}

/**
 * Get the number of scenarios for a station.
 * Uses the loaded scenarios array if available, otherwise falls back to scenarioCount.
 */
export function getScenarioCount(station: Station): number {
  if (station.scenarios !== undefined) {
    return station.scenarios.length;
  }
  return station.scenarioCount;
}

/**
 * Get the total size of all scenarios in megabytes.
 */
export function getTotalSizeMB(station: Station): number {
  return station.totalSizeBytes / (1024 * 1024);
}

/**
 * Get a human-readable size string for the station's total size.
 */
export function getFormattedTotalSize(station: Station): string {
  const mb = getTotalSizeMB(station);
  if (mb < 1) {
    const kb = station.totalSizeBytes / 1024;
    return `${kb.toFixed(1)} KB`;
  }
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  }
  return `${mb.toFixed(1)} MB`;
}

/**
 * Get a scenario by its ID from the station.
 * Returns undefined if scenarios are not loaded or the scenario is not found.
 */
export function getScenarioById(station: Station, id: string): Scenario | undefined {
  return station.scenarios?.find((scenario) => scenario.id === id);
}

/**
 * Get a random scenario from the station.
 * Returns undefined if scenarios are not loaded or the station has no scenarios.
 */
export function getRandomScenario(station: Station): Scenario | undefined {
  if (!station.scenarios || station.scenarios.length === 0) {
    return undefined;
  }
  const randomIndex = Math.floor(Math.random() * station.scenarios.length);
  return station.scenarios[randomIndex];
}

/**
 * Check if a station is valid (has all required fields).
 */
export function isValidStation(station: Partial<Station>): station is Station {
  return Boolean(
    station.id &&
    station.airportCode &&
    station.stationType &&
    station.displayName &&
    typeof station.scenarioCount === 'number' &&
    typeof station.totalSizeBytes === 'number' &&
    station.manifestUrl &&
    station.updatedAt instanceof Date
  );
}

/**
 * Create a new Station entity.
 * Throws if required fields are missing.
 */
export function createStation(input: {
  id: string;
  airportCode: string;
  stationType: string;
  displayName: string;
  airportName?: string | null;
  iataCode?: string | null;
  icaoCode?: string | null;
  localCode?: string | null;
  city?: string | null;
  country?: string | null;
  timeZone?: string | null;
  radioEffectPreset?: AtcRadioEffectPresetInput;
  scenarioCount: number;
  totalSizeBytes: number;
  manifestUrl: string;
  updatedAt: Date | string;
  scenarios?: Scenario[];
}): Station {
  if (
    !input.id ||
    !input.airportCode ||
    !input.stationType ||
    !input.displayName ||
    !input.manifestUrl
  ) {
    throw new Error('Station is missing required fields');
  }

  const updatedAt =
    input.updatedAt instanceof Date
      ? input.updatedAt
      : new Date(input.updatedAt);

  return {
    id: input.id,
    airportCode: input.airportCode,
    stationType: input.stationType,
    displayName: input.displayName,
    airportName: input.airportName,
    iataCode: input.iataCode,
    icaoCode: input.icaoCode,
    localCode: input.localCode,
    city: input.city,
    country: input.country,
    timeZone: input.timeZone,
    radioEffectPreset: input.radioEffectPreset,
    scenarioCount: input.scenarioCount,
    totalSizeBytes: input.totalSizeBytes,
    manifestUrl: input.manifestUrl,
    updatedAt,
    scenarios: input.scenarios ? Object.freeze([...input.scenarios]) : undefined,
  };
}

/**
 * Create a new Station with loaded scenarios.
 */
export function withScenarios(station: Station, scenarios: Scenario[]): Station {
  return {
    ...station,
    scenarios: Object.freeze([...scenarios]),
  };
}
